import React, { useState, useEffect, useCallback, useMemo } from 'react';
import ModalPortal from '../UI/ModalPortal';
import { X, Image, Calendar, TrendingUp, ExternalLink } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import statisticsService from '../../services/statisticsService';
import { Loader } from '../UI/LoadingSpinner';
import StatCard from '../Dashboard/StatCard';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import StatisticsTable from './StatisticsTable';

const formatDate = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const ManagerPortraitDetailsModal = ({ manager, filters, onClose }) => {
  const location = useLocation();

  const portraitItemRowKey = useCallback(
    (item) => item.id ?? `${item.file_name}-${item.uploaded_at}`,
    []
  );

  const recentPortraitsColumns = useMemo(() => [
    {
      key: 'file_name',
      label: 'File Name',
      render: (item) => (
        <span className="text-sm text-gray-900 dark:text-gray-100">{item.file_name || `Portrait #${item.id}`}</span>
      ),
    },
    {
      key: 'talent',
      label: 'Talent',
      render: (item) => item.talent_id && item.talent_name ? (
        <Link to={`/talents/${item.talent_id}`} state={{ from: location.pathname + location.search }} className="flex items-center gap-1 text-primary-600 visited:text-purple-700 dark:visited:text-purple-400 hover:text-primary-700">
          {item.talent_name}<ExternalLink className="h-3 w-3" />
        </Link>
      ) : <span className="text-sm text-gray-500 dark:text-gray-400">—</span>,
    },
    {
      key: 'uploaded_at',
      label: 'Uploaded',
      render: (item) => (
        <span className="text-sm text-gray-600 dark:text-gray-400">{formatDate(item.uploaded_at)}</span>
      ),
    },
  ], [location.pathname, location.search]);

  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const isDark = document.documentElement.classList.contains('dark');
  const chartGridColor = isDark ? '#374151' : '#e5e7eb';
  const chartAxisColor = isDark ? '#9ca3af' : '#6b7280';
  const tooltipBg = isDark ? '#1f2937' : '#ffffff';
  const tooltipBorder = isDark ? '#374151' : '#e5e7eb';

  useEffect(() => { fetchDetails(); }, [manager.manager_id]);

  const fetchDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await statisticsService.getManagerPortraitDetails(manager.manager_id, {
        startDate: filters.startDate,
        endDate: filters.endDate
      });
      setDetails(response.data || response);
    } catch (err) {
      setError(err.message || 'Failed to load details');
      toast.error('Failed to load portrait details');
    } finally {
      setLoading(false);
    }
  };


  const formatTimelineDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <ModalPortal onClose={onClose} ariaLabel="Manager portrait details">
      <div className="flex max-h-[calc(100dvh-1.5rem)] w-full min-w-0 max-w-4xl flex-col overflow-hidden rounded-lg bg-white shadow-xl dark:bg-gray-800 sm:max-h-[calc(100dvh-4rem)]">
        <div className="flex min-w-0 shrink-0 flex-wrap items-start justify-between gap-3 border-b border-gray-200 p-4 dark:border-gray-700 sm:p-6">
          <div className="flex min-w-0 items-center gap-3">
            <Image className="h-6 w-6 text-purple-500" />
            <div>
              <h2 className="break-words text-2xl font-bold text-gray-900 dark:text-gray-100">Portrait Statistics</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">{manager.manager?.name || 'Unknown Manager'}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
            <X className="h-6 w-6 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        <div className="min-h-0 min-w-0 flex-1 overflow-y-auto p-4 sm:p-6">
          {loading ? (
            <div className="flex justify-center items-center py-12"><Loader /></div>
          ) : error ? (
            <div className="text-center py-8 text-red-600 dark:text-red-400">{error}</div>
          ) : details ? (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <StatCard title="Total Portraits" value={details.total_portraits || 0} icon={Image} color="purple" />
                <StatCard title="Date Range" value={`${filters.startDate} - ${filters.endDate}`} icon={Calendar} color="blue" />
              </div>

              {details.timeline && details.timeline.length > 0 && (
                <div className="bg-gray-50 dark:bg-gray-900 min-w-0 overflow-hidden rounded-lg p-4 sm:p-6">
                  <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100 flex items-center gap-2">
                    <TrendingUp className="h-5 w-5" />
                    Upload Timeline
                  </h3>
                  <ResponsiveContainer width="100%" height={300} minWidth={0}>
                    <LineChart data={details.timeline.map(t => ({ date: formatTimelineDate(t.date), count: t.count }))}>
                      <CartesianGrid strokeDasharray="3 3" stroke={chartGridColor} />
                      <XAxis dataKey="date" tick={{ fill: chartAxisColor, fontSize: 12 }} angle={-45} textAnchor="end" height={80} />
                      <YAxis tick={{ fill: chartAxisColor, fontSize: 12 }} />
                      <Tooltip contentStyle={{ backgroundColor: tooltipBg, border: `1px solid ${tooltipBorder}`, borderRadius: '0.5rem', color: isDark ? '#f9fafb' : '#111827' }} />
                      <Line type="monotone" dataKey="count" stroke="#a855f7" strokeWidth={2} name="Portraits Uploaded" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}

              <div className="min-w-0 rounded-lg bg-gray-50 p-4 dark:bg-gray-900 sm:p-6">
                <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">Recent Portraits</h3>
                <StatisticsTable
                  columns={recentPortraitsColumns}
                  data={details.recent_portraits || []}
                  rowKey={portraitItemRowKey}
                  variant="ghost"
                  emptyState={
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">No recent portraits found</div>
                  }
                />
              </div>
            </div>
          ) : null}
        </div>

        <div className="flex shrink-0 flex-wrap justify-end gap-2 border-t border-gray-200 p-4 pb-[max(1rem,env(safe-area-inset-bottom))] dark:border-gray-700 sm:p-6">
          <button onClick={onClose} className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors">
            Close
          </button>
        </div>
      </div>
    </ModalPortal>
  );
};

export default ManagerPortraitDetailsModal;
